import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Visibility as VisibilityIcon,
  AddCircle as AddCircleIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import DropzoneArea from '../../common/DropzoneArea';
import {
  Attachment,
  BillOfMaterialWithRelations,
  EntityIdentity,
  useDeleteBomByIdMutation,
  useGetBomByIdQuery,
  useGetBomQuery,
  useGetDevelopmentsQuery,
  usePatchBomByIdMutation,
  usePostBomMutation,
} from '../../../redux/vmsApi';
import { formatCurrency } from '../../../utils/currencyUtil';

type BomRecord = BillOfMaterialWithRelations & {
  title?: string;
  totalBudget?: number;
};

type BomFormState = {
  title: string;
  developmentId: string;
  projectId: string;
  attachments: Attachment[];
};

const initialFormState: BomFormState = {
  title: '',
  developmentId: '',
  projectId: '',
  attachments: [],
};

const BomManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: developments = [], isLoading: developmentsLoading } = useGetDevelopmentsQuery();
  const { data: boms = [], isLoading: bomsLoading, isError: isBomsError } = useGetBomQuery({});
  const [createBom, { isLoading: isCreating }] = usePostBomMutation();
  const [updateBom, { isLoading: isUpdating }] = usePatchBomByIdMutation();
  const [deleteBom, { isLoading: isDeleting }] = useDeleteBomByIdMutation();

  const [formState, setFormState] = useState<BomFormState>(initialFormState);
  const [editingBomId, setEditingBomId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<BomRecord | null>(null);
  const [viewBomId, setViewBomId] = useState<string | null>(null);
  const [attachmentBomId, setAttachmentBomId] = useState<string | null>(null);
  const [dialogAttachments, setDialogAttachments] = useState<Attachment[]>([]);
  const [isSavingAttachments, setIsSavingAttachments] = useState(false);

  const {
    data: selectedBom,
    isLoading: selectedBomLoading,
  } = useGetBomByIdQuery({ id: viewBomId ?? '' }, { skip: !viewBomId });

  const selectedDevelopment = developments.find((development) => String(development.id) === formState.developmentId);
  const projects = selectedDevelopment?.projects ?? [];
  const selectedProject = projects.find((project) => String(project.id) === formState.projectId);
  const isSubmitting = isCreating || isUpdating;
  const isEditing = Boolean(editingBomId);
  const canSubmit = Boolean(formState.title.trim() && formState.developmentId && formState.projectId) && !isSubmitting;

  useEffect(() => {
    if (!feedback) {
      return;
    }

    const timer = window.setTimeout(() => {
      setFeedback(null);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [feedback]);

  const setField = <K extends keyof BomFormState>(field: K, value: BomFormState[K]) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormState(initialFormState);
    setEditingBomId(null);
  };

  const mapBomToFormState = (bom: BomRecord): BomFormState => ({
    title: bom.title ?? '',
    developmentId: String(bom.development?.id ?? ''),
    projectId: String(bom.project?.id ?? ''),
    attachments: bom.attachments ?? [],
  });

  const buildPayload = () => {
    const development: EntityIdentity = {
      id: selectedDevelopment?.id,
      name: selectedDevelopment?.name,
    };
    const project: EntityIdentity = {
      id: selectedProject?.id,
      name: selectedProject?.name,
    };

    return {
      title: formState.title.trim(),
      development,
      project,
      attachments: formState.attachments,
      active: false,
    };
  };

  const handleDevelopmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const developmentId = event.target.value;

    setFormState((current) => ({
      ...current,
      developmentId,
      projectId: '',
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canSubmit) {
      return;
    }

    try {
      const payload = buildPayload();

      if (editingBomId) {
        await updateBom({
          id: editingBomId,
          billOfMaterialPartial: payload as never,
        }).unwrap();
        setFeedback({ severity: 'success', message: 'BOM updated successfully.' });
      } else {
        await createBom({
          newBillOfMaterial: payload as never,
        }).unwrap();
        setFeedback({ severity: 'success', message: 'BOM created successfully.' });
      }

      resetForm();
    } catch (error) {
      console.error('Failed to save BOM:', error);
      setFeedback({ severity: 'error', message: 'Failed to save BOM. Please try again.' });
    }
  };

  const handleOpenAttachmentDialog = (bom: BomRecord) => {
    setAttachmentBomId(bom.id ?? null);
    setDialogAttachments(bom.attachments ?? []);
  };

  const handleSaveAttachments = async () => {
    if (!attachmentBomId) {
      return;
    }

    setIsSavingAttachments(true);
    try {
      await updateBom({
        id: attachmentBomId,
        billOfMaterialPartial: { attachments: dialogAttachments } as never,
      }).unwrap();
      setFeedback({ severity: 'success', message: 'Attachments updated successfully.' });
      setAttachmentBomId(null);
    } catch (error) {
      console.error('Failed to update attachments:', error);
      setFeedback({ severity: 'error', message: 'Failed to update attachments. Please try again.' });
    } finally {
      setIsSavingAttachments(false);
    }
  };

  const handleEdit = (bom: BomRecord) => {
    if (bom.active || !bom.id) {
      return;
    }

    setEditingBomId(bom.id);
    setFormState(mapBomToFormState(bom));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleActive = async (bom: BomRecord) => {
    if (!bom.id) {
      return;
    }

    try {
      await updateBom({
        id: bom.id,
        billOfMaterialPartial: { active: !bom.active } as never,
      }).unwrap();
      setFeedback({
        severity: 'success',
        message: `BOM ${bom.active ? 'deactivated' : 'activated'} successfully.`,
      });
      if (editingBomId === bom.id && bom.active) {
        resetForm();
      }
    } catch (error) {
      console.error('Failed to toggle BOM status:', error);
      setFeedback({ severity: 'error', message: 'Failed to change BOM status. Please try again.' });
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate?.id) {
      return;
    }

    try {
      await deleteBom({ id: deleteCandidate.id }).unwrap();
      setFeedback({ severity: 'success', message: 'BOM deleted successfully.' });
      if (editingBomId === deleteCandidate.id) {
        resetForm();
      }
      setDeleteCandidate(null);
    } catch (error) {
      console.error('Failed to delete BOM:', error);
      setFeedback({ severity: 'error', message: 'Failed to delete BOM. Please try again.' });
    }
  };

  const renderAttachments = (attachments: Attachment[] | undefined) => {
    if (!attachments?.length) {
      return <Typography color="text.secondary">No supporting documents uploaded.</Typography>;
    }

    return (
      <Stack spacing={1}>
        {attachments.map((attachment, index) => (
          <Button
            key={`${attachment.url}-${index}`}
            variant="text"
            sx={{ justifyContent: 'flex-start', px: 0 }}
            onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
          >
            {attachment.name}
          </Button>
        ))}
      </Stack>
    );
  };

  return (
    <ProcurementContainer>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#123456' }}>
            BOM Management
          </Typography>
          <Typography color="text.secondary">
            Create and manage bills of material.
          </Typography>
        </Box>

        {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

        <Card elevation={3}>
          <CardContent>
            <Stack component="form" spacing={3} onSubmit={handleSubmit}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {isEditing ? 'Update BOM' : 'Create New BOM'}
                </Typography>
                <Typography color="text.secondary">
                  This follows the requisition flow: title, development, project, supporting documents, then submit.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formState.title}
                  onChange={(event) => setField('title', event.target.value)}
                />

                <TextField
                  fullWidth
                  select
                  label="Development"
                  value={formState.developmentId}
                  onChange={handleDevelopmentChange}
                  disabled={developmentsLoading}
                >
                  {developments.map((development) => (
                    <MenuItem key={String(development.id)} value={String(development.id)}>
                      {development.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  label="Project"
                  value={formState.projectId}
                  onChange={(event) => setField('projectId', event.target.value)}
                  disabled={!formState.developmentId}
                >
                  {projects.map((project) => (
                    <MenuItem key={String(project.id)} value={String(project.id)}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Supporting Documents
                </Typography>
                <DropzoneArea
                  key={`bom-attachments-${editingBomId ?? 'create'}-${formState.attachments.length}`}
                  fieldName="attachments"
                  attachments={formState.attachments}
                  setAttachments={(attachments) => setField('attachments', attachments)}
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {isEditing && (
                  <Button variant="outlined" onClick={resetForm} disabled={isSubmitting}>
                    Cancel
                  </Button>
                )}
                <Button variant="contained" type="submit" disabled={!canSubmit}>
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update BOM' : 'Create BOM'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Paper elevation={2}>
          <Stack spacing={2} p={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                BOM List
              </Typography>
              <Typography color="text.secondary">
                Active BOMs can be deactivated, but they cannot be edited or deleted until inactive again.
              </Typography>
            </Box>

            {isBomsError && <Alert severity="error">Failed to load BOMs.</Alert>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Development</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Total Budget</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bomsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" py={4}>
                          <CircularProgress size={24} />
                          <Typography>Loading BOMs...</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : boms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography color="text.secondary" py={3} textAlign="center">
                          No BOMs found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    boms.map((bom) => {
                      const record = bom as BomRecord;

                      return (
                        <TableRow key={record.id ?? `${record.development?.id}-${record.project?.id}`} hover>
                          <TableCell>{record.title || 'Untitled BOM'}</TableCell>
                          <TableCell>{record.development?.name || '-'}</TableCell>
                          <TableCell>{record.project?.name || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(Number(record.totalBudget ?? 0), 'usd')}</TableCell>
                          <TableCell>{record.attachments?.length ?? 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.active ? 'Active' : 'Inactive'}
                              color={record.active ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Tooltip title="Manage Items">
                                <IconButton onClick={() => navigate(`/bom/management/${record.id}/items`)}>
                                  <AddCircleIcon fontSize="small" color="primary" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View BOM">
                                <IconButton onClick={() => setViewBomId(record.id ?? null)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Manage Attachments">
                                <IconButton onClick={() => handleOpenAttachmentDialog(record)}>
                                  <AttachFileIcon fontSize="small" color="action" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={record.active ? 'Deactivate BOM' : 'Activate BOM'}>
                                <IconButton onClick={() => handleToggleActive(record)} disabled={isUpdating}>
                                  {record.active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" color="success" />}
                                </IconButton>
                              </Tooltip>
                              {!record.active && (
                                <Tooltip title="Edit BOM">
                                  <IconButton onClick={() => handleEdit(record)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!record.active && (
                                <Tooltip title="Delete BOM">
                                  <IconButton onClick={() => setDeleteCandidate(record)} disabled={isDeleting}>
                                    <DeleteIcon fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={Boolean(viewBomId)} onClose={() => setViewBomId(null)} fullWidth maxWidth="md">
        <DialogTitle>BOM Details</DialogTitle>
        <DialogContent dividers>
          {selectedBomLoading ? (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={24} />
              <Typography>Loading BOM details...</Typography>
            </Stack>
          ) : selectedBom ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography>{(selectedBom as BomRecord).title || 'Untitled BOM'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Development</Typography>
                <Typography>{selectedBom.development?.name || '-'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Project</Typography>
                <Typography>{selectedBom.project?.name || '-'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedBom.active ? 'Active' : 'Inactive'}
                  color={selectedBom.active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Supporting Documents</Typography>
                {renderAttachments(selectedBom.attachments)}
              </Box>
            </Stack>
          ) : (
            <Alert severity="warning">Unable to load this BOM.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewBomId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(attachmentBomId)}
        onClose={() => !isSavingAttachments && setAttachmentBomId(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Manage Attachments</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              Upload new documents or remove existing ones. Changes are saved when you click &quot;Save Attachments&quot;.
            </Typography>
            <DropzoneArea
              key={`attachment-dialog-${attachmentBomId}`}
              fieldName="attachments"
              attachments={dialogAttachments}
              setAttachments={setDialogAttachments}
            />
            {dialogAttachments.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Documents ({dialogAttachments.length})</Typography>
                <Stack spacing={1}>
                  {dialogAttachments.map((attachment, index) => (
                    <Stack key={`${attachment.url}-${index}`} direction="row" alignItems="center" spacing={1}>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ justifyContent: 'flex-start', flexGrow: 1, px: 0, textAlign: 'left' }}
                        onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                      >
                        {attachment.name}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={isSavingAttachments}
                        onClick={() =>
                          setDialogAttachments((prev) => prev.filter((_, i) => i !== index))
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentBomId(null)} disabled={isSavingAttachments}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAttachments}
            disabled={isSavingAttachments}
          >
            {isSavingAttachments ? 'Saving...' : 'Save Attachments'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete BOM</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{deleteCandidate?.title || 'this BOM'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCandidate(null)} disabled={isDeleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete BOM'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProcurementContainer>
  );
};

export default BomManagementPage;